import { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';
import { collection, doc, setDoc, addDoc, query, orderBy, onSnapshot, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../firebase';
import type { User } from 'firebase/auth';

function formatMsgTime(seconds: number): string {
  if (!seconds) return '';
  const d = new Date(seconds * 1000);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

interface Message {
  id: string;
  text: string;
  from: 'user' | 'admin';
  createdAt: number;
}

interface ChatWidgetProps {
  user: User;
  inline?: boolean;
  onClose: () => void;
}

export default function ChatWidget({ user, inline, onClose }: ChatWidgetProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  // The first time messages arrive (chat just opened) we jump to the bottom
  // instantly so it looks preloaded; later messages animate in smoothly.
  const didInitialScroll = useRef(false);

  const chatRef = collection(db, 'chats', user.uid, 'messages');

  useEffect(() => {
    // Reset unread count for user when chat is opened
    setDoc(doc(db, 'chats', user.uid), { unreadUser: 0 }, { merge: true }).catch(() => {});
  }, [user.uid]);

  useEffect(() => {
    const q = query(collection(db, 'chats', user.uid, 'messages'), orderBy('createdAt', 'asc'));
    return onSnapshot(q,
      (snap) => {
        setMessages(snap.docs.map((d) => ({
          id: d.id,
          text: d.data().text,
          from: d.data().from,
          createdAt: d.data().createdAt?.seconds || 0,
        })));
      },
      () => { /* silently ignore — usually parent doc not yet created or auth in flux */ }
    );
  }, [user.uid]);

  useEffect(() => {
    if (messages.length === 0) return;
    bottomRef.current?.scrollIntoView({ behavior: didInitialScroll.current ? 'smooth' : 'auto' });
    didInitialScroll.current = true;
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    try {
      // Ensure parent doc exists + increment admin unread count
      await setDoc(doc(db, 'chats', user.uid), {
        userName: user.displayName || 'User',
        userEmail: user.email || '',
        updatedAt: serverTimestamp(),
        unreadAdmin: increment(1),
      }, { merge: true });
      await addDoc(chatRef, {
        text,
        from: 'user',
        createdAt: serverTimestamp(),
        userName: user.displayName || 'User',
        userEmail: user.email || '',
      });
    } catch { /* ignore */ }
    setSending(false);
  };

  const body = (
    <div className={`chat-widget${inline ? ' chat-widget--inline' : ''}`} onClick={(e) => e.stopPropagation()}>
      <div className="chat-messages">
        <div className="chat-msg chat-msg--admin chat-msg--intro">
          <span>Questions, account or billing help, or anything else: message the developer here. Feature ideas and bug reports go to the Discussion board. Replies can take a few days.</span>
        </div>
        {messages.map((m) => (
          <div key={m.id} className={`chat-msg chat-msg--${m.from}`}>
            <span>{m.text}</span>
            <span className="chat-msg-time">{formatMsgTime(m.createdAt)}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form className="chat-input-bar" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
        <input
          className="chat-input"
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <button className="chat-send" type="submit" disabled={!input.trim() || sending}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );

  if (inline) return body;

  return (
    <div className="chat-backdrop" onClick={onClose}>
      {body}
    </div>
  );
}
